import { useState, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';
import type { SocialAccountData } from '../social-accounts-table-types';
import type { SocialAccountsColumn } from '../social-accounts-column-dialog';

export function useSocialAccountsTable({
  data,
  isLoading = false,
  isRefreshing = false,
  pageSize = 50,
  selectedColumns,
  setSelectedColumns,
}: {
  data: SocialAccountData[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  pageSize?: number;
  selectedColumns: SocialAccountsColumn[];
  setSelectedColumns: (
    columns: SocialAccountsColumn[] | ((prev: SocialAccountsColumn[]) => SocialAccountsColumn[]),
  ) => void;
}) {
  const location = useLocation();
  const history = useHistory();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Get current page from URL or default to 1
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Update URL when page changes
  const setCurrentPage = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('page', page.toString());
      history.replace({ pathname: location.pathname, search: newSearchParams.toString() });
    },
    [history, location.pathname, location.search],
  );

  // Handle column drag and drop
  const handleColumnDragEnd = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;

      setSelectedColumns((prevColumns) => {
        const sortedColumns = [...prevColumns].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const movedColumn = sortedColumns.splice(fromIndex, 1)[0];
        sortedColumns.splice(toIndex, 0, movedColumn);

        // Update order property for all columns
        return sortedColumns.map((col, index) => ({
          ...col,
          order: index + 1,
        }));
      });
    },
    [setSelectedColumns],
  );

  // Handle column visibility toggle
  const handleToggleColumnCheckBox = useCallback(
    (columnId: string) => {
      setSelectedColumns((prevColumns) => {
        return prevColumns.map((col) => (col.id === columnId ? { ...col, hidden: !col.hidden } : col));
      });
    },
    [setSelectedColumns],
  );

  // Handle select all/none
  const handleToggleSelectall = useCallback(
    (checked: boolean) => {
      setSelectedColumns((prevColumns) => {
        return prevColumns.map((col) => ({ ...col, hidden: !checked }));
      });
    },
    [setSelectedColumns],
  );

  const filteredData = useMemo(() => {
    if (!searchValue) return data;
    return data.filter((row) => {
      return (
        row.socialAccount?.toLowerCase().includes(searchValue.toLowerCase()) ||
        row.memberName?.toLowerCase().includes(searchValue.toLowerCase()) ||
        row.roas?.toLowerCase().includes(searchValue.toLowerCase())
      );
    });
  }, [data, searchValue]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // Reset to page 1 if current page is beyond total pages (e.g., after filtering)
  const adjustedCurrentPage = Math.min(currentPage, totalPages || 1);

  const paginatedData = useMemo(() => {
    const start = (adjustedCurrentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, adjustedCurrentPage, pageSize]);

  // Calculate record range for display
  const startRecord = useMemo(() => (adjustedCurrentPage - 1) * pageSize + 1, [adjustedCurrentPage, pageSize]);
  const endRecord = useMemo(
    () => Math.min(adjustedCurrentPage * pageSize, filteredData.length),
    [adjustedCurrentPage, pageSize, filteredData.length],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      setCurrentPage(1);
    },
    [setCurrentPage],
  );

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(Math.max(adjustedCurrentPage - 1, 1));
  }, [adjustedCurrentPage, setCurrentPage]);

  const handleNextPage = useCallback(() => {
    setCurrentPage(Math.min(adjustedCurrentPage + 1, totalPages));
  }, [adjustedCurrentPage, totalPages, setCurrentPage]);

  const handleExport = useCallback(async (_type: 'current' | 'full') => {
    setIsExporting(true);
    // Implement export logic here
    setTimeout(() => setIsExporting(false), 1000);
  }, []);

  const socialAccountsTableProps = {
    data: paginatedData,
    isLoading,
    isRefreshing,
    sorting,
    setSorting,
    searchValue,
    setSearchValue,
    currentPage: adjustedCurrentPage,
    setCurrentPage,
    isExporting,
    setIsExporting,
    pageSize,
    filteredData,
    totalPages,
    startRecord,
    endRecord,
    handleSearchChange,
    handlePreviousPage,
    handleNextPage,
    handleExport,
    handleColumnDragEnd,
    selectedColumns,
    setSelectedColumns,
    handleToggleColumnCheckBox,
    handleToggleSelectall,
  };

  return {
    socialAccountsTableProps,
    sorting,
    setSorting,
    searchValue,
    setSearchValue,
    currentPage: adjustedCurrentPage,
    setCurrentPage,
    isExporting,
    setIsExporting,
    pageSize,
    filteredData,
    paginatedData,
    totalPages,
    startRecord,
    endRecord,
    handleSearchChange,
    handlePreviousPage,
    handleNextPage,
    handleExport,
    handleColumnDragEnd,
    selectedColumns,
    setSelectedColumns,
    handleToggleColumnCheckBox,
    handleToggleSelectall,
  };
}
